import type { LabeledValue } from 'antd/es/select'
import type { RawValue } from 'src/components/declare'
import type { EasyRemoteSearch } from 'src/components/common/declare'
import type { EditControlProps } from '../../declare'
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react'
import { autorun, reaction } from 'mobx'
import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { ListTableContext, EditContext, EasyRemoteSelect, EasyModal, FormPage, EditPage } from 'src/components'
import { appStore } from 'src/stores'
import { $api } from 'src/plugins'
import { getNavFieldsFromNavField, getSaveFieldFromNavField, getRulesFromNavFilters } from 'src/components/root/helper'
import { initFilter, extendOrders, initGroup, extendGroups, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { uuid, validGUID } from 'src/utils/tools'
import { get } from 'lodash'

const ExtraRender = (props: {
  config: EditControlProps['config'],
  onClose: (data: any) => void
}) => {
  const edit = useContext(EditContext)

  const [extraSrcForm, setExtraSrcForm] = useState<any>()
  useEffect(() => reaction(
    () => get(edit.formBase, props.config.SHOW_ID_FIELD),
    formId => {
      const { SHOW_FORM_CODE: formCode } = props.config
      formCode && formId && setExtraSrcForm({ formCode, formId })
    }
  ), [props.config.SHOW_FORM_CODE, props.config.SHOW_ID_FIELD])

  // 配置了快捷编辑表单
  const extraEditForm = useMemo(() => {
    const { EDIT_FORM_CODE: formCode, EDIT_FORM_FILTER: field, EDIT_FORM_FILTER_VALUE: value } = props.config
    if (formCode) {
      const target: any = { formCode }
      // 分页下拉组件快捷编辑表单添加的过滤条件，通过此过滤条件，达到表单编辑的效果，而不是新增效果
      let payload = undefined
      if (field && value) {
        const { OrgId } = appStore.current.headers
        payload = initFilter({ OrgId })
        extendRules(payload, [getSingleRule(field, 1, value)])
      }
      payload && (target.formFilter = payload)
      return target
    }
    return null
  }, [props.config.EDIT_FORM_CODE, props.config.EDIT_FORM_FILTER, props.config.EDIT_FORM_FILTER_VALUE])

  return (
    <React.Fragment>
      {
        extraSrcForm
          ? (
            <EasyModal
              modal={{ width: '100%' }}
              slot={<InfoCircleOutlined className='fz-20 pd-l8' />}
            >
              <FormPage
                formCode={extraSrcForm.formCode}
                formData={{ formId: extraSrcForm.formId, formDisabled: true }}
              />
            </EasyModal>
          )
          : null
      }
      {
        extraEditForm
          ? (
            <EasyModal
              modal={{ title: extraEditForm.formCode, width: '100%' }}
              slot={<PlusCircleOutlined className='fz-20 pd-l8' />}
              onClose={props.onClose}
            >
              <EditPage formCode={extraEditForm.formCode} />
            </EasyModal>
          )
          : null
      }
    </React.Fragment>
  )
}

const PaginationSelect = (props: EditControlProps) => {
  const table = useContext(ListTableContext)
  const edit = useContext(EditContext)

  // 配置的过滤字段是否变化，变化则需要重新加载分页组件
  const [refreshKey, setRefreshKey] = useState(uuid())
  useEffect(() => {
    let timer: any = -1
    const dispose = autorun(() => {
      const { Nav_Filters } = props.config
      const filters = getRulesFromNavFilters(Nav_Filters, {
        user: appStore.current.userinfo,
        formBase: edit.formBase,
        record: table?.selectedRow
      })
      clearTimeout(timer)
      timer = setTimeout(() => {
        filters.length && setRefreshKey(JSON.stringify(filters))
      }, 1000)
    })

    return () => {
      dispose()
      clearTimeout(timer)
    }
  }, [props.config.Nav_Filters])

  // 分页下拉数据
  const source = useRef([])
  const getSource = (params: EasyRemoteSearch) => {
    const {
      INPUT_DATA_API: url,
      INPUT_FILTER_ORG_TYPE: OrgType,
      INPUT_FILTER_FIELD,
      INPUT_NAV_FIELD,
      Nav_Filters,
      INPUT_SORT_FIELD,
      INPUT_SORT_ORDER,
    } = props.config
    if (!url) return new Promise(resolve => { resolve(false) })

    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, OrgType, PageIndex: params.current })
    // 存在多字段排序
    const Orders: { Field: string, Order: number }[] = []
    if (INPUT_SORT_FIELD) {
      const sorts = INPUT_SORT_FIELD.split(',')
      const orders = INPUT_SORT_ORDER?.split(',') ?? []
      sorts.forEach((field: string, index: number) => {
        Orders.push({ Field: field, Order: Number(orders[index] ?? 0) })
      })
      extendOrders(payload, Orders)
    }
    // 搜索过滤字段
    const filterFields: string[] = INPUT_FILTER_FIELD?.split(',') || ['NAME', 'CODE']
    const value = params.value ?? ''
    const Groups: any = initGroup(false)
    String(value) && Groups.Rules.push(...filterFields.map(field => getSingleRule(field, 9, value as RawValue)))
    extendGroups(payload, [Groups])
    // navFields 导航字段
    const navFields = getNavFieldsFromNavField(INPUT_NAV_FIELD)
    navFields.length && extendInclude(payload, navFields)
    // 配置的过滤字段
    const filters = getRulesFromNavFilters(Nav_Filters, {
      user: appStore.current.userinfo,
      formBase: edit.formBase,
      record: table?.selectedRow
    })
    extendRules(payload, filters)

    return $api['app/getDataByPost'](payload, {
      url,
      fullData: true
    }).then((res: any) => {
      source.current = res?.Data ?? []
      return res
    })
  }

  const handleChange = (data: LabeledValue | undefined) => {
    const extraFields = getSaveFieldFromNavField(data?.value, source.current, props.config)
    // 表单数据
    props.onChange?.(data?.value, extraFields)
  }

  const handleExtraClose = (data: any) => {
    data?.ID && getSource({ value: data.NAME, current: 1 }).then(() => {
      handleChange({ label: data.NAME, value: data.ID })
    })
  }

  // 下拉显示字段，用于 Option 的 label 显示
  const optionLabelFields = useMemo(() => {
    return props.config.INPUT_SHOW_FIELD?.split(',') ?? ['NAME']
  }, [props.config.INPUT_SHOW_FIELD])

  // 回填显示字段，用于选中后回填回 Select 框
  const customLabelFields = useMemo(() => {
    return props.config.INPUT_LABEL_FIELD?.split(',')
  }, [props.config.INPUT_LABEL_FIELD])

  const selectedValue = useMemo(() => {
    if (props.value === undefined) return undefined
    const isValueGUID = validGUID(props.value)
    return { label: !isValueGUID ? props.value : undefined, value: props.value }
  }, [props.value])

  const { __READONLY__, EDITABLE, PROMPT_INFO } = props.config
  return (
    <EasyRemoteSelect
      key={refreshKey}
      value={selectedValue}
      remoteMethod={getSource}
      onChange={val => handleChange(val as LabeledValue | undefined)}
      selectProps={{
        disabled: __READONLY__ || !EDITABLE || props.evtConfig?.disabled,
        placeholder: PROMPT_INFO
      }}
      fieldOption={{ labels: optionLabelFields, inputLabels: customLabelFields }}
      addonAfter={<ExtraRender config={props.config} onClose={handleExtraClose} />}
    />
  )
}

export default PaginationSelect